<template>
  <div class="chart-container">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';

// Register all Chart.js components
Chart.register(...registerables);

export default {
  name: 'ClientRankingChart',
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      chart: null,
      isCreating: false
    };
  },
  mounted() {
    this.$nextTick(() => {
      // Delay initialization like other dashboard charts
      setTimeout(() => {
        this.createChart();
      }, 100);
    });
  },
  beforeUnmount() {
    this.destroyChart();
  },
  watch: {
    data: {
      handler() {
        this.updateChart();
      },
      deep: true
    }
  },
  methods: {
    createChart() {
      // Prevent multiple simultaneous chart creations
      if (this.isCreating) {
        return;
      }
      
      this.isCreating = true;
      
      try {
        // Always destroy existing chart first to prevent conflicts
        this.destroyChart();
        
        // Validate data first
        if (!this.data || this.data.length === 0) {
          return;
        }

        if (!this.$refs.chartCanvas) {
          return;
        }
        
        // Get context with error handling
        let ctx;
        try {
          ctx = this.$refs.chartCanvas.getContext('2d');
        } catch (contextError) {
          console.error('ClientRankingChart: Error getting canvas context:', contextError);
          return;
        }
        
        if (!ctx) {
          return;
        }

        // Sort data by value (count) in descending order and take top 10
        const sortedData = [...this.data]
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10);

        const labels = sortedData.map(item => item.label || item.client_name || 'Unknown Client');
        const values = sortedData.map(item => item.value || 0);

        this.chart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              label: 'Error Count',
              data: values,
              backgroundColor: [
                '#0a3954',
                '#dc3545',
                '#ffc107',
                '#0a1821',
                '#082B3F',
                '#364b5c',
                '#17a2b8',
                '#6c757d',
                '#28a745',
                '#007bff'
              ],
              borderColor: '#ffffff',
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false, // Disable animations to prevent context conflicts
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  padding: 20,
                  usePointStyle: true,
                  font: {
                    size: 12
                  }
                }
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const index = context.dataIndex;
                    const item = sortedData[index];
                    return [
                      `Client: ${item?.label || item?.client_name || 'Unknown'}`,
                      `Count: ${context.parsed.toLocaleString()}`,
                      `Percentage: ${item?.percentage?.toFixed(1) || 0}%`
                    ];
                  }
                }
              }
            }
          }
        });
      } catch (error) {
        console.error('ClientRankingChart: Error creating chart:', error);
      } finally {
        this.isCreating = false;
      }
    },

    updateChart() {
      if (!this.chart) {
        this.createChart();
        return;
      }
      
      try {
        // Validate data first
        if (!this.data || this.data.length === 0) {
          return;
        }

        // Sort data by value (count) in descending order and take top 10
        const sortedData = [...this.data]
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10);

        const labels = sortedData.map(item => item.label || item.client_name || 'Unknown Client');
        const values = sortedData.map(item => item.value || 0);

        this.chart.data.labels = labels;
        this.chart.data.datasets[0].data = values;
        
        this.chart.update('none'); // Use 'none' animation mode for updates
      } catch (error) {
        console.error('ClientRankingChart: Error updating chart:', error);
        this.destroyChart();
        this.$nextTick(() => {
          this.createChart();
        });
      }
    },

    destroyChart() {
      if (this.chart) {
        try {
          this.chart.destroy();
        } catch (error) {
          console.error('ClientRankingChart: Error destroying chart:', error);
        } finally {
          this.chart = null;
        }
      }
    }
  }
};
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 350px;
  width: 100%;
}
</style>
